/* ============================================
   EmojiPicker — Custom emoji grid picker
   Zero dependencies, ~200 most-used emojis,
   categorised with search/filter.
   ============================================ */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { IconSearch } from '@/components/icons/Icons'

interface EmojiPickerProps {
  onSelect: (emoji: string) => void
  onClose: () => void
}

type EmojiCategory = { name: string; icon: string; emojis: string[] }

const EMOJI_DATA: EmojiCategory[] = [
  {
    name: 'Caritas',
    icon: '😀',
    emojis: ['😀','😃','😄','😁','😅','😂','🤣','🥲','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚','😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🥸','🤩','🥳','😏','😒','😔','😕','🙁','😟','😢','😭','😤','😡','🤬','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓','🫠','😶','😐','😑','😬','🫢','🫣','🤥','🤫','🤗','🤔','🫡','🤭','🥱','😴','😪','🤤','😵','🤐','🥴','🤢','🤮','🤧','😷','🤒','🤕','🤑','🤠','😈','👿','👹','👺','🤡','💩','👻','💀','👽','👾','🤖'],
  },
  {
    name: 'Gestos',
    icon: '👍',
    emojis: ['👍','👎','👏','🙌','👐','🤲','🤝','🤜','🤛','✊','👊','🫶','🤞','✌️','🤟','🤘','👌','🤌','🤏','👈','👉','👆','👇','☝️','🖕','🫵','✋','🤚','🖐️','🖖','👋','🤙','🦾','🦿','💪','🫳','🫴','🙏','✍️','💅','🤳','🦻','🦶','🦵','🫦','👅','👂','👃','👣','👀','👁️','🧠','🫀','🫁','🦷','🦴','🗣️','👤','👥'],
  },
  {
    name: 'Objetos',
    icon: '💡',
    emojis: ['💼','🎒','👓','🕶️','🥽','🌂','🧳','📱','💻','⌚','📷','🎥','🎤','🎧','🎮','🕹️','💡','🔦','📖','📚','💰','💳','💎','🔨','🔧','🔌','🔋','📬','📦','✂️','📌','🔑','🗝️','🛡️','⚙️','⚖️','🔗','💉','💊','🩹','🩺','🧬','🔭','🔬','📡','🛰️','🚀','✈️','🚗','🚲','🏍️'],
  },
  {
    name: 'Corazones',
    icon: '❤️',
    emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💝','💘','💌','🫶','💟','♥️'],
  },
  {
    name: 'Naturaleza',
    icon: '🌸',
    emojis: ['🌸','🌺','🌻','🌹','🌷','🌼','💐','🥀','🍀','🌿','🌱','🌵','🌴','🌳','🌲','🍂','🍁','🍃','☀️','🌤️','⛅','🌦️','🌈','⚡','🔥','💧','⭐','🌟','✨','🎉','🎊','🎂','🍰','🎈','🎁','🏆','🥇','🎖️'],
  },
  {
    name: 'Comida',
    icon: '🍕',
    emojis: ['🍎','🍊','🍋','🍉','🍇','🍓','🍒','🥑','🍌','🍕','🍔','🌮','🌯','🍣','🍜','🍩','🍪','🎂','🍰','🍫','🍿','☕','🍵','🍺','🍷','🥂','🧃','🥤','🧊'],
  },
]

const ALL_EMOJIS = EMOJI_DATA.flatMap(c => c.emojis)

export default function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState(0)
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const filteredEmojis = useMemo(() => {
    if (!search.trim()) {
      return EMOJI_DATA[activeCategory]?.emojis || []
    }
    const q = search.toLowerCase()
    return ALL_EMOJIS.filter(e => {
      // Unicode-based search: try to match common emoji descriptions
      const desc = EMOJI_DATA.find(c => c.emojis.includes(e))?.name || ''
      return desc.toLowerCase().includes(q)
    }).slice(0, 60)
  }, [search, activeCategory])

  const handleEmojiClick = useCallback((emoji: string) => {
    onSelect(emoji)
  }, [onSelect])

  return (
    <div className="emoji-picker" ref={containerRef}>
      {/* Search bar */}
      <div className="emoji-picker__search">
        <IconSearch size={14} />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Buscar emoji..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="emoji-picker__search-input"
        />
      </div>

      {/* Category tabs (only when not searching) */}
      {!search.trim() && (
        <div className="emoji-picker__categories">
          {EMOJI_DATA.map((cat, i) => (
            <button
              key={cat.name}
              className={`emoji-picker__cat-btn ${i === activeCategory ? 'emoji-picker__cat-btn--active' : ''}`}
              onClick={() => setActiveCategory(i)}
              type="button"
              title={cat.name}
            >
              {cat.icon}
            </button>
          ))}
        </div>
      )}

      {/* Emoji grid */}
      <div className="emoji-picker__grid">
        {filteredEmojis.map((emoji, i) => (
          <button
            key={`${i}-${emoji}`}
            className="emoji-picker__item"
            onClick={() => handleEmojiClick(emoji)}
            type="button"
            title={emoji}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}
